import { useMemo } from 'react';
import useSWRMutation from 'swr/mutation';
import { SWR_KEYS } from 'constants/constants';
import { createToolInspectionReportUrl } from 'services/suscription/tool-inspection.service';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';

export interface IUseOpenToolInspectionReportResult {
  openFile: (productId: string) => Promise<void>;
  isGeneratingUrl: boolean;
}

/**
 * Hook para generar y abrir el reporte de inspección de una herramienta.
 * Maneja estados de carga, notificaciones y errores de bloqueo de pop-ups.
 */
export function useOpenToolInspectionReport(): IUseOpenToolInspectionReportResult {
  const { trigger, isMutating: isGeneratingUrl } = useSWRMutation(
    SWR_KEYS.createPdfToolInformation,
    async (_key: string, { arg: productId }: { arg: string }) => {
      return createToolInspectionReportUrl(productId);
    },
    {
      onSuccess: url => {
        if (!url) return;

        const newWindow = window.open(url, '_blank');

        if (newWindow) {
          newWindow.opener = null;
          displaySuccessNotification({
            customJSXMessage: <>PDF generado exitosamente</>
          });
          return;
        }

        displayErrorNotification({
          customJSXMessage: (
            <>
              No se pudo abrir el PDF. Por favor, <b>permita los pop-ups</b> en este sitio.
            </>
          )
        });
      },
      onError: () => {
        displayErrorNotification({
          customJSXMessage: <>Ocurrió un error al intentar generar el reporte.</>
        });
      }
    }
  );

  const openFile = useMemo(
    () => async (productId: string) => {
      if (!productId) return;

      await trigger(productId);
    },
    [trigger]
  );

  return {
    openFile,
    isGeneratingUrl
  };
}
